/**
 * RLS (Row Level Security) 에러 중앙 핸들러
 *
 * Supabase에서 발생하는 RLS 관련 에러를 감지하고 일관되게 처리합니다.
 * 모든 Supabase 클라이언트(browser, server)에서 공통으로 사용됩니다.
 *
 * 기능:
 * - RLS 에러 감지 (PostgreSQL 에러 코드 + 메시지 패턴)
 * - 상세 정보 콘솔 로깅 (테이블, 작업, 요청 URL 등)
 * - 사용자 친화적 에러 메시지 생성
 */

// RLS 관련 PostgreSQL 에러 코드
const RLS_ERROR_CODES = [
  '42501', // insufficient_privilege - RLS policy violation
  '42000', // syntax_error_or_access_rule_violation
  'PGRST301', // Permission denied
  'PGRST302', // Permission denied for schema
]

// RLS 에러 메시지 패턴
const RLS_ERROR_PATTERNS = [
  'row-level security',
  'permission denied',
  'violates row-level security policy',
  'new row violates row-level security',
  'RLS',
]

export interface RLSErrorInfo {
  isRLSError: boolean
  code?: string
  message?: string
  table?: string
  operation?: string
}

export interface RLSErrorContext {
  /** 요청 URL (Supabase API 엔드포인트) */
  url?: string
  /** HTTP 메서드 */
  method?: string
  /** 요청 바디 (민감 정보 제외) */
  requestBody?: Record<string, unknown>
  /** 호출한 서비스 함수명 */
  functionName?: string
  /** 추가 컨텍스트 */
  extra?: Record<string, unknown>
}

/**
 * 응답 데이터에서 RLS 에러 여부를 감지합니다.
 */
export function detectRLSError(data: unknown): RLSErrorInfo {
  if (!data || typeof data !== 'object') {
    return { isRLSError: false }
  }

  const errorData = data as Record<string, unknown>

  // code 체크
  const code = errorData.code as string | undefined
  if (code && RLS_ERROR_CODES.includes(code)) {
    return {
      isRLSError: true,
      code,
      message: errorData.message as string,
      table: extractTableName(errorData.message as string),
      operation: extractOperation(errorData.message as string),
    }
  }

  // message 패턴 체크
  const message = (errorData.message as string) || ''
  const hint = (errorData.hint as string) || ''
  const details = (errorData.details as string) || ''
  const combinedText = `${message} ${hint} ${details}`.toLowerCase()

  for (const pattern of RLS_ERROR_PATTERNS) {
    if (combinedText.includes(pattern.toLowerCase())) {
      return {
        isRLSError: true,
        code,
        message,
        table: extractTableName(message),
        operation: extractOperation(message),
      }
    }
  }

  return { isRLSError: false }
}

/**
 * 에러 메시지에서 테이블 이름을 추출합니다.
 */
function extractTableName(message?: string): string | undefined {
  if (!message) return undefined

  // "permission denied for table students" 패턴
  const tableMatch = message.match(/(?:table|relation)\s+["']?(\w+)["']?/i)
  if (tableMatch) return tableMatch[1]

  // "on students" 패턴
  const onMatch = message.match(/\bon\s+["']?(\w+)["']?/i)
  if (onMatch) return onMatch[1]

  return undefined
}

/**
 * 에러 메시지에서 작업 유형을 추출합니다.
 */
function extractOperation(message?: string): string | undefined {
  if (!message) return undefined

  const lowerMessage = message.toLowerCase()

  if (lowerMessage.includes('select')) return 'SELECT'
  if (lowerMessage.includes('insert')) return 'INSERT'
  if (lowerMessage.includes('update')) return 'UPDATE'
  if (lowerMessage.includes('delete')) return 'DELETE'

  return undefined
}

/**
 * RLS 에러용 사용자 친화적 메시지를 생성합니다.
 */
export function getRLSErrorMessage(info: RLSErrorInfo): string {
  const table = info.table ? ` (${info.table})` : ''
  const operation = info.operation ? `[${info.operation}] ` : ''

  return `${operation}접근 권한이 없습니다${table}. 관리자에게 문의하세요.`
}

/**
 * Supabase fetch 응답에서 RLS 에러를 감지합니다.
 * Response를 클론하여 본문을 읽으므로 원본 Response는 영향받지 않습니다.
 */
export async function checkResponseForRLSError(
  response: Response
): Promise<RLSErrorInfo> {
  try {
    // 응답이 성공이면 RLS 에러 아님
    if (response.ok) {
      return { isRLSError: false }
    }

    // 403, 401 상태 코드 체크
    if (response.status === 403 || response.status === 401) {
      const cloned = response.clone()
      try {
        const data = await cloned.json()
        const rlsInfo = detectRLSError(data)
        if (rlsInfo.isRLSError) {
          return rlsInfo
        }
      } catch {
        // JSON 파싱 실패 시 무시
      }
    }

    return { isRLSError: false }
  } catch {
    return { isRLSError: false }
  }
}

/**
 * 서버 사이드에서 RLS 에러를 로깅합니다.
 */
export function logRLSError(info: RLSErrorInfo, context?: string): void {
  console.error('[RLS ERROR]', {
    context,
    code: info.code,
    message: info.message,
    table: info.table,
    operation: info.operation,
    timestamp: new Date().toISOString(),
  })
}

/**
 * URL에서 테이블명을 추출합니다.
 * Supabase REST API URL 패턴: /rest/v1/{table_name}
 */
function extractTableFromUrl(url: string): string | undefined {
  try {
    const urlObj = new URL(url)
    const pathMatch = urlObj.pathname.match(/\/rest\/v1\/(\w+)/)
    return pathMatch?.[1]
  } catch {
    // URL 파싱 실패 시
    const pathMatch = url.match(/\/rest\/v1\/(\w+)/)
    return pathMatch?.[1]
  }
}

/**
 * RLS 에러 발생 시 상세 진단 정보를 콘솔에 남깁니다.
 * 브라우저/서버 모두에서 사용 가능한 통합 함수입니다.
 */
export function reportRLSError(
  info: RLSErrorInfo,
  context?: RLSErrorContext
): void {
  // RLS 에러가 아니면 무시
  if (!info.isRLSError) return

  // 테이블명이 없으면 요청 URL에서 추출 시도
  const table = info.table || (context?.url ? extractTableFromUrl(context.url) : undefined)

  console.error(
    `[RLS] ${info.operation || 'ACCESS'} denied on ${table || 'unknown table'}`,
    {
      code: info.code,
      message: info.message,
      table,
      operation: info.operation,
      url: context?.url,
      method: context?.method,
      functionName: context?.functionName,
      ...context?.extra,
      timestamp: new Date().toISOString(),
    }
  )
}
